import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CheckBox} from '../../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {RecurringEventSaveTypesEnum} from '../../../../store/data/calendarEventVersions/calendarEventVersionsActions';
import {OkCancelDialog} from '../../../common/dialogs/okCancelDialog';
import {RecurringSaveTypeSelector} from '../../editor/recurring/recurringSaveTypeSelector';
import {DeleteConfirmHandler} from '../calendarEventViewerHeaderButtons';

/*
 * Props.
 */

interface DeleteCalendarEventDialogProps {
  /* Whether we're deleting a recurring event. */
  isRecurring: boolean;
  /* Handler for confirming a delete action. */
  onConfirmClick: DeleteConfirmHandler;
  /* Handler for canceling a delete action. */
  onRequestClose: Handler;
  /* Whether to show the option of checking whether notifications are sent. */
  shouldShowNotifyGuestsOption: boolean;
  /* Number of guests the notification would be sent to. */
  nonResourceGuestCount: number;
  /* Handler to toggle whether or not to notify guests. */
  toggleShouldNotifyGuests: Handler;
  /* Whether or not to notify guests. */
  shouldNotifyGuests: boolean;
}

/*
 * Styles.
 */

interface StyledCheckboxDivProps {
  $shouldAddLeftPadding: boolean;
}

const StyledCheckboxDiv = styled.div<StyledCheckboxDivProps>`
  display: flex;
  padding-top: 15px;
  padding-left: ${(p) => (p.$shouldAddLeftPadding ? `5px` : ``)};
`;

const StyledCheckboxTextDiv = styled.div`
  padding-left: 8px;
`;

/*
 * Component.
 */

export const DeleteCalendarEventDialog: FC<DeleteCalendarEventDialogProps> = (props) => {
  const {isRecurring, onConfirmClick} = props;

  const [recurringSaveType, setRecurringSaveType] = useState(RecurringEventSaveTypesEnum.INSTANCE);

  const onOkClick = isRecurring ? () => onConfirmClick(recurringSaveType) : onConfirmClick;

  const title = isRecurring ? (
    <Trans id="recurring-dialog-delete-title">Delete a recurring event</Trans>
  ) : (
    <Trans id="delete-event-title">Delete calendar event</Trans>
  );

  return (
    <OkCancelDialog
      title={title}
      okButtonLabel={<Trans id="delete-event-delete">Delete</Trans>}
      okButtonVariant={ButtonVariantsEnum.DANGER}
      onOkClick={onOkClick}
      onRequestClose={props.onRequestClose}
    >
      {isRecurring ? (
        <RecurringSaveTypeSelector recurringSaveType={recurringSaveType} onChange={setRecurringSaveType} />
      ) : (
        <Trans id="delete-event-warning">
          Are you sure you want to delete this event? This action cannot be undone.
        </Trans>
      )}

      {props.shouldShowNotifyGuestsOption && (
        <StyledCheckboxDiv $shouldAddLeftPadding={isRecurring}>
          <CheckBox onChange={props.toggleShouldNotifyGuests} isChecked={props.shouldNotifyGuests} />
          <StyledCheckboxTextDiv>
            <Plural
              id="delete-event-email-guests"
              value={props.nonResourceGuestCount}
              one="Email guest"
              other="Email guests"
            />
          </StyledCheckboxTextDiv>
        </StyledCheckboxDiv>
      )}
    </OkCancelDialog>
  );
};
